import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    const status = error.response?.status;

    if (status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } else if (status === 403) {
      toast.error('You do not have permission to perform this action');
    } else if (status === 429) {
      toast.error('Too many requests. Please try again later.');
    } else if (status >= 500) {
      toast.error('Server error. Please try again later.');
    }

    return Promise.reject({ status, message, errors: error.response?.data?.errors });
  }
);

// ═══════════════════════════════════════════════════════════════
//  AUTH API
// ═══════════════════════════════════════════════════════════════
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  refreshToken: () => api.post('/auth/refresh'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  // Multi-org endpoints
  switchOrg: (data) => api.post('/auth/switch-org', data),
  getOrganizations: () => api.get('/auth/organizations'),
};

// ═══════════════════════════════════════════════════════════════
//  CUSTOMERS API
// ═══════════════════════════════════════════════════════════════
export const customersAPI = {
  list: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  getOverview: (id) => api.get(`/customers/${id}/overview`),
  getContacts: (id) => api.get(`/customers/${id}/contacts`),
  addContact: (id, data) => api.post(`/customers/${id}/contacts`, data),
  updateContact: (id, contactId, data) => api.put(`/customers/${id}/contacts/${contactId}`, data),
  removeContact: (id, contactId) => api.delete(`/customers/${id}/contacts/${contactId}`),
  getSubscriptions: (id, params) => api.get(`/customers/${id}/subscriptions`, { params }),
  getInvoices: (id, params) => api.get(`/customers/${id}/invoices`, { params }),
  getPayments: (id, params) => api.get(`/customers/${id}/payments`, { params }),
};

// ═══════════════════════════════════════════════════════════════
//  PRODUCTS API
// ═══════════════════════════════════════════════════════════════
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// ═══════════════════════════════════════════════════════════════
//  PLANS API
// ═══════════════════════════════════════════════════════════════
export const plansAPI = {
  list: (params) => api.get('/plans', { params }),
  getById: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
};

// ═══════════════════════════════════════════════════════════════
//  ADDONS API
// ═══════════════════════════════════════════════════════════════
export const addonsAPI = {
  list: (params) => api.get('/addons', { params }),
  getById: (id) => api.get(`/addons/${id}`),
  create: (data) => api.post('/addons', data),
  update: (id, data) => api.put(`/addons/${id}`, data),
  delete: (id) => api.delete(`/addons/${id}`),
};

// ═══════════════════════════════════════════════════════════════
//  COUPONS API
// ═══════════════════════════════════════════════════════════════
export const couponsAPI = {
  list: (params) => api.get('/coupons', { params }),
  getById: (id) => api.get(`/coupons/${id}`),
  create: (data) => api.post('/coupons', data),
  update: (id, data) => api.put(`/coupons/${id}`, data),
  delete: (id) => api.delete(`/coupons/${id}`),
  validate: (data) => api.post('/coupons/validate', data),
};

// ═══════════════════════════════════════════════════════════════
//  SUBSCRIPTIONS API
// ═══════════════════════════════════════════════════════════════
export const subscriptionsAPI = {
  list: (params) => api.get('/subscriptions', { params }),
  getById: (id) => api.get(`/subscriptions/${id}`),
  create: (data) => api.post('/subscriptions', data),
  update: (id, data) => api.put(`/subscriptions/${id}`, data),
  delete: (id) => api.delete(`/subscriptions/${id}`),
  cancel: (id, data) => api.post(`/subscriptions/${id}/cancel`, data),
  pause: (id, data) => api.post(`/subscriptions/${id}/pause`, data),
  resume: (id, data) => api.post(`/subscriptions/${id}/resume`, data),
  reactivate: (id) => api.post(`/subscriptions/${id}/reactivate`),
  extend: (id, data) => api.post(`/subscriptions/${id}/extend`, data),
  addCharge: (id, data) => api.post(`/subscriptions/${id}/add-charge`, data),
  addOneTimeAddon: (id, data) => api.post(`/subscriptions/${id}/add-one-time-addon`, data),
  changeNextBillingDate: (id, data) => api.put(`/subscriptions/${id}/next-billing-date`, data),
  manageContacts: (id, data) => api.put(`/subscriptions/${id}/contacts`, data),
  changePaymentMode: (id, data) => api.put(`/subscriptions/${id}/payment-mode`, data),
  bulkInvoicePreference: (data) => api.post('/subscriptions/bulk-invoice-preference', data),
};

// ═══════════════════════════════════════════════════════════════
//  INVOICES API
// ═══════════════════════════════════════════════════════════════
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  send: (id, data) => api.post(`/invoices/${id}/send`, data),
  markAsSent: (id) => api.post(`/invoices/${id}/mark-as-sent`),
  recordPayment: (id, data) => api.post(`/invoices/${id}/record-payment`, data),
  applyCredit: (id, data) => api.post(`/invoices/${id}/apply-credit`, data),
  void: (id) => api.post(`/invoices/${id}/void`),
  writeOff: (id) => api.post(`/invoices/${id}/write-off`),
  getPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  sendReminder: (id) => api.post(`/invoices/${id}/send-reminder`),
  bulkSend: (data) => api.post('/invoices/bulk-send', data),
};

// ═══════════════════════════════════════════════════════════════
//  PAYMENTS API
// ═══════════════════════════════════════════════════════════════
export const paymentsAPI = {
  list: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  refund: (id, data) => api.post(`/payments/${id}/refund`, data),
};

// ═══════════════════════════════════════════════════════════════
//  CREDIT NOTES API
// ═══════════════════════════════════════════════════════════════
export const creditNotesAPI = {
  list: (params) => api.get('/creditnotes', { params }),
  getById: (id) => api.get(`/creditnotes/${id}`),
  create: (data) => api.post('/creditnotes', data),
  update: (id, data) => api.put(`/creditnotes/${id}`, data),
  void: (id) => api.post(`/creditnotes/${id}/void`),
  allocate: (id, data) => api.post(`/creditnotes/${id}/allocate`, data),
};

// ═══════════════════════════════════════════════════════════════
//  TAXES API
// ═══════════════════════════════════════════════════════════════
export const taxesAPI = {
  list: (params) => api.get('/taxes', { params }),
  getById: (id) => api.get(`/taxes/${id}`),
  create: (data) => api.post('/taxes', data),
  update: (id, data) => api.put(`/taxes/${id}`, data),
  delete: (id) => api.delete(`/taxes/${id}`),
};

// ═══════════════════════════════════════════════════════════════
//  WEBHOOKS API
// ═══════════════════════════════════════════════════════════════
export const webhooksAPI = {
  list: (params) => api.get('/webhooks', { params }),
  getById: (id) => api.get(`/webhooks/${id}`),
  create: (data) => api.post('/webhooks', data),
  update: (id, data) => api.put(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  getEvents: (id, params) => api.get(`/webhooks/${id}/events`, { params }),
};

// ═══════════════════════════════════════════════════════════════
//  SETTINGS API
// ═══════════════════════════════════════════════════════════════
export const settingsAPI = {
  getOrganization: () => api.get('/settings/organization'),
  updateOrganization: (data) => api.put('/settings/organization', data),
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/settings', data),
  getEmailTemplates: () => api.get('/settings/email-templates'),
  updateEmailTemplate: (id, data) => api.put(`/settings/email-templates/${id}`, data),
};

// ═══════════════════════════════════════════════════════════════
//  REPORTS API
// ═══════════════════════════════════════════════════════════════
export const reportsAPI = {
  dashboard: (params) => api.get('/reports/dashboard', { params }),
  subscriptions: (params) => api.get('/reports/subscriptions', { params }),
  revenue: (params) => api.get('/reports/revenue', { params }),
  mrr: (params) => api.get('/reports/mrr', { params }),
  churn: (params) => api.get('/reports/churn', { params }),
  invoiceAging: (params) => api.get('/reports/invoice-aging', { params }),
  accountsReceivable: (params) => api.get('/reports/accounts-receivable', { params }),
  customerLTV: (params) => api.get('/reports/customer-ltv', { params }),
};

// ═══════════════════════════════════════════════════════════════
//  INTEGRATIONS API
// ═══════════════════════════════════════════════════════════════
export const integrationsAPI = {
  connect: (provider) => api.get(`/integrations/${provider}/connect`),
  connectWithToken: (provider, accessToken) => api.post(`/integrations/${provider}/connect`, { access_token: accessToken }),
  disconnect: (provider) => api.post(`/integrations/${provider}/disconnect`),
  getStatus: (provider) => api.get(`/integrations/${provider}/status`),
  sync: (provider, entity, direction = 'outbound') => api.post(`/integrations/${provider}/sync`, { entity_type: entity, direction }),
  getSyncLogs: (provider, params) => api.get(`/integrations/${provider}/sync-logs`, { params }),
  updateSettings: (provider, data) => api.put(`/integrations/${provider}/settings`, data),
};

// ═══════════════════════════════════════════════════════════════
//  TEAM API
// ═══════════════════════════════════════════════════════════════
export const teamAPI = {
  list: () => api.get('/team'),
  invite: (data) => api.post('/team/invite', data),
  updateRole: (userId, data) => api.put(`/team/${userId}/role`, data),
  remove: (userId) => api.delete(`/team/${userId}`),
  acceptInvite: (data) => api.post('/team/accept-invite', data),
};

// ═══════════════════════════════════════════════════════════════
//  API KEYS API
// ═══════════════════════════════════════════════════════════════
export const apiKeysAPI = {
  list: () => api.get('/api-keys'),
  create: (data) => api.post('/api-keys', data),
  revoke: (id) => api.delete(`/api-keys/${id}`),
};

// ═══════════════════════════════════════════════════════════════
//  RESPONSE NORMALIZER
// ═══════════════════════════════════════════════════════════════
// The backend returns entity-specific keys (e.g. { products: [...], page_context: {...} })
// but the frontend pages expect a generic format: { data: [...], pagination: {...} }
// This helper normalizes list responses so pages can use data?.data and data?.pagination.
const ENTITY_KEYS = [
  'products', 'plans', 'addons', 'coupons',
  'customers', 'subscriptions', 'invoices', 'payments',
  'credit_notes', 'taxes', 'webhooks', 'contacts',
  'templates', 'sync_logs',
];

function normalizeListResponse(response) {
  if (!response || typeof response !== 'object') return response;

  // Find the entity array key
  let dataKey = null;
  for (const key of ENTITY_KEYS) {
    if (Array.isArray(response[key])) {
      dataKey = key;
      break;
    }
  }

  if (!dataKey) return response; // not a list response, return as-is

  // Build normalized response, keeping all original fields
  const normalized = { ...response };
  normalized.data = response[dataKey];

  // Normalize pagination: page_context → pagination
  if (response.page_context) {
    normalized.pagination = response.page_context;
  }

  return normalized;
}

// Wrap list methods to normalize responses
function wrapList(listFn) {
  return async (params) => {
    const response = await listFn(params);
    return normalizeListResponse(response);
  };
}

// Apply normalization to all API list methods
customersAPI.list = wrapList((params) => api.get('/customers', { params }));
productsAPI.list = wrapList((params) => api.get('/products', { params }));
plansAPI.list = wrapList((params) => api.get('/plans', { params }));
addonsAPI.list = wrapList((params) => api.get('/addons', { params }));
couponsAPI.list = wrapList((params) => api.get('/coupons', { params }));
subscriptionsAPI.list = wrapList((params) => api.get('/subscriptions', { params }));
invoicesAPI.list = wrapList((params) => api.get('/invoices', { params }));
paymentsAPI.list = wrapList((params) => api.get('/payments', { params }));
creditNotesAPI.list = wrapList((params) => api.get('/creditnotes', { params }));
taxesAPI.list = wrapList((params) => api.get('/taxes', { params }));

export default api;
